import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { getTheme, applyTheme } from "@/lib/theme";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import DeviceDetail from "@/pages/DeviceDetail";
import MediaLibrary from "@/pages/Media";
import Tenants from "@/pages/Tenants";
import TenantDetail from "@/pages/TenantDetail";
import AuditLog from "@/pages/AuditLog";
import SettingsPage from "@/pages/Settings";
import Groups from "@/pages/Groups";
import Updates from "@/pages/Updates";
import Support from "@/pages/Support";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="font-mono text-xs uppercase tracking-widest text-cyan-400 animate-pulse">
          Verbinde mit Control Plane…
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/passwort-aendern") {
    return <Navigate to="/passwort-aendern" replace />;
  }
  if (!user.must_change_password && location.pathname === "/passwort-aendern") {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  const [theme, setThemeState] = useState(getTheme());
  useEffect(() => {
    applyTheme(getTheme());
    const handler = () => setThemeState(getTheme());
    window.addEventListener("themechange", handler);
    return () => window.removeEventListener("themechange", handler);
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme={theme}
          position="bottom-right"
          toastOptions={{
            style: {
              background: theme === "dark" ? "#0F172A" : "#FFFFFF",
              border: "1px solid rgba(6,182,212,0.35)",
              color: theme === "dark" ? "#F8FAFC" : "#0F172A",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "12px",
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/passwort-aendern"
            element={
              <Protected>
                <ChangePassword />
              </Protected>
            }
          />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="devices/:id" element={<DeviceDetail />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="gruppen" element={<Groups />} />
            <Route path="updates" element={<Updates />} />
            <Route path="support" element={<Support />} />
            <Route path="tenants" element={<Tenants />} />
            <Route path="tenants/:id" element={<TenantDetail />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="einstellungen" element={<SettingsPage />} />
            <Route path="devices" element={<Navigate to="/" replace />} />
            <Route path="pairing" element={<Navigate to="/" replace />} />
            <Route path="playlists" element={<Navigate to="/media" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
